import {
  autoUpdate,
  flip,
  offset,
  type Placement,
  safePolygon,
  shift,
  size,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
} from '@floating-ui/react'
import { useCallback, useRef, useState } from 'react'

import { useStayOpenWhileResizing } from './useStayOpenWhileResizing'

export const useTooltip = ({
  placement = 'bottom-start',
  offset: offsetAmount = 4,
  hasMinWidthOfAnchorWidth = false,
  hoverDelay = 200,
  isInteractive = false,
}: {
  /**
   * Placement of the tooltip
   * @defaultValue `'bottom-start'`
   */
  placement?: Placement
  /**
   * Offset in px to apply between the tooltip and the anchor element.
   * The default ensures animations look good and there's no overlap / flicker
   * @defaultValue `4`
   */
  offset?: number
  /**
   * True if the tooltip should have a minimum width equal to the anchor element
   * @defaultValue `false`
   */
  hasMinWidthOfAnchorWidth?: boolean
  /**
   * Determine whether or not the tooltip is interactive, meaning it can be hovered over and the elements inside it can be interacted with.
   * If this is set to true, the tooltip will not close when the user hovers over it.
   */
  isInteractive?: boolean
  /**
   * Delay in ms before tooltip shows when hovering over the anchor element
   * The default is shorter than title attribute which is too slow but fast enough so when mouse roaming you don't get a flash of all the tooltips
   * @defaultValue `200`
   */
  hoverDelay?: number
} = {}) => {
  const [isOpen, setIsOpen] = useState(false)
  const isHoldingCloseRef = useRef(false)
  const close = useCallback(() => setIsOpen(false), [])
  const strategy = 'fixed' as const // needed to avoid jumpy scrollable parents
  const {
    context,
    refs,
    x,
    y,
    elements,
    placement: calculatedPlacement,
  } = useFloating({
    open: isOpen,
    onOpenChange: (open) => {
      // A close arriving while a tip the reader is working in lays itself out
      // again is the tip moving out from under them, not the reader leaving it.
      if (!open && isHoldingCloseRef.current) {
        return
      }

      setIsOpen(open)
    },
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(offsetAmount),
      flip({
        padding: {
          top: offsetAmount,
          bottom: offsetAmount,
          left: offsetAmount,
          right: offsetAmount,
        },
      }),
      shift({
        padding: {
          top: offsetAmount,
          bottom: offsetAmount,
          left: offsetAmount,
          right: offsetAmount,
        },
      }),
      // A tip is only ever as big as the side it was placed on can hold, so it
      // never hangs off the edge of the screen with whatever it carries. What
      // does not fit is scrolled to within the tip instead.
      size({
        padding: {
          top: offsetAmount,
          bottom: offsetAmount,
          left: offsetAmount,
          right: offsetAmount,
        },
        apply({ rects, availableWidth, availableHeight, elements }) {
          if (hasMinWidthOfAnchorWidth) {
            elements.floating.style.minWidth = `${rects.reference.width}px`
          }

          // The room left on the side the tip was placed on is published as
          // custom properties rather than set as styles because the element
          // that is positioned is not the one that is drawn: the paper is its
          // child, and inherits them.
          elements.floating.style.setProperty(
            '--amaze-tooltip-available-width',
            `${Math.max(availableWidth, 0)}px`,
          )
          elements.floating.style.setProperty(
            '--amaze-tooltip-available-height',
            `${Math.max(availableHeight, 0)}px`,
          )
        },
      }),
    ],
    strategy,
    placement,
  })

  useStayOpenWhileResizing({
    isHoldingCloseRef,
    isOpen: isOpen && isInteractive,
    floating: elements.floating,
    reference: elements.domReference,
    close,
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useHover(context, {
      restMs: hoverDelay,
      handleClose: isInteractive
        ? safePolygon() // allows for user to move mouse into tooltip without it disappearing
        : undefined,
    }),
    useFocus(context),
  ])

  return {
    refs,
    x,
    y,
    context,
    getReferenceProps,
    getFloatingProps,
    isOpen,
    setIsOpen,
    strategy,
    calculatedPlacement,
  }
}
